import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    GlobalStyles,
    Grid,
    Icon,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme
} from "@mui/material";
import {useMemo, useState} from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import {useDashboardEnvios} from "../../api/DpgeIntegracaoEsfingeApi";
import Page from "../../components/layout/page/Page";
import PageHeader from "../../components/page-header/PageHeader";
import ResumoFolhaCompetencia from "../../components/ocorrencia/ResumoFolhaCompetencia";
import {AMBIENTE, NOME_APP} from "../../shared/env";
import {
    obterAmbienteAplicacao,
    nomeAplicacaoNeutro
} from "../../shared/modoAplicacao";
import {
    canAccessFinanceira,
    canAccessFolha,
    canAccessMaster
} from "../../shared/keycloak";
import {baixarCsvDashboard} from "./dashboardExport";
import {formatarCompetencia} from "../../shared/ocorrenciaTemporalUtil";

const MODULOS = [
    {valor: "FOLHA", rotulo: "Folha e Atos de Pessoal"},
    {valor: "FINANCEIRA", rotulo: "Financeiro"},
    {valor: "GERAL", rotulo: "Serviços gerais"}
];
const DESTINOS = [
    {valor: "ESFINGE_DIRETO", rotulo: "e-Sfinge direto"},
    {valor: "FORNECEDOR", rotulo: "Fornecedor"},
    {valor: "DESABILITADO", rotulo: "Entrega desabilitada"}
];
const CORES_ESTADO = {
    CANCELADA: "#616161",
    PROCESSADO_COM_SUCESSO: "#2e7d32",
    PROCESSADO_COM_ERRO: "#d32f2f",
    RESULTADO_INDETERMINADO: "#ed6c02",
    PROCESSAMENTO: "#0288d1",
    EM_FILA: "#616161",
    AGUARDANDO_APROVACAO: "#7b1fa2"
};

function formatarData(data) {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const dia = String(data.getDate()).padStart(2, "0");
    return `${ano}-${mes}-${dia}`;
}

function filtroPadrao() {
    const fim = new Date();
    const inicio = new Date();
    inicio.setDate(inicio.getDate() - 29);
    return {
        tipoConsulta: "PERIODO",
        competencia: formatarData(fim).slice(0, 7),
        dataInicial: formatarData(inicio),
        dataFinal: formatarData(fim),
        modulos: [],
        destinos: [],
        incluirArquivados: false
    };
}

function moeda(valor) {
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL"
    }).format(Number(valor || 0));
}

function numero(valor) {
    return new Intl.NumberFormat("pt-BR").format(Number(valor || 0));
}

function dataExibicao(valor) {
    if (!valor) {
        return "Não informada";
    }
    const [ano, mes, dia] = valor.split("-");
    return `${dia}/${mes}/${ano}`;
}

function parametrosConsulta(filtro) {
    const {tipoConsulta, competencia, dataInicial, dataFinal, ...comuns} =
        filtro;
    return tipoConsulta === "COMPETENCIA"
        ? {...comuns, competencia: competencia.replace("-", "")}
        : {...comuns, dataInicial, dataFinal};
}

function rotuloConsulta(filtro) {
    return filtro.tipoConsulta === "COMPETENCIA"
        ? formatarCompetencia(filtro.competencia.replace("-", ""))
        : `${dataExibicao(filtro.dataInicial)} a ${dataExibicao(
              filtro.dataFinal
          )}`;
}

function selecaoExibicao(valores, opcoes, todos) {
    if (!valores.length) {
        return todos;
    }
    return opcoes
        .filter((item) => valores.includes(item.valor))
        .map((item) => item.rotulo)
        .join(", ");
}

function validarPeriodo(dataInicial, dataFinal) {
    if (!dataInicial || !dataFinal) {
        return "Informe as duas datas do período";
    }
    if (dataInicial > dataFinal) {
        return "A data final deve ser igual ou posterior à data inicial";
    }
    const inicio = new Date(`${dataInicial}T00:00:00`);
    const fim = new Date(`${dataFinal}T00:00:00`);
    const dias = Math.round((fim - inicio) / 86400000) + 1;
    return dias > 366 ? "O período não pode ultrapassar 366 dias" : "";
}

function Kpi({icone, rotulo, valor, cor = "primary.main"}) {
    return (
        <Card
            component="section"
            aria-label={rotulo}
            className="dashboard-kpi"
            variant="outlined"
            sx={{height: "100%"}}
        >
            <CardContent
                sx={{
                    minHeight: 118,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    "&:last-child": {pb: 2}
                }}
            >
                <Icon sx={{color: cor, mb: 0.5}}>{icone}</Icon>
                <Typography variant="h4" component="p" fontWeight={700}>
                    {numero(valor)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {rotulo}
                </Typography>
            </CardContent>
        </Card>
    );
}

function ResumoCategoriaFolha({item}) {
    const icones = {
        ATIVOS: "badge",
        INATIVOS: "elderly",
        PENSIONISTAS: "family_restroom"
    };
    return (
        <Card variant="outlined" sx={{height: "100%", borderRadius: 2}}>
            <CardContent>
                <Stack spacing={1.25}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Icon color="primary">
                            {icones[item.categoria] || "groups"}
                        </Icon>
                        <Box>
                            <Typography fontWeight={700}>
                                {item.rotulo}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {numero(item.servidores)} beneficiários ·{" "}
                                {numero(item.lancamentos)} lançamentos
                            </Typography>
                        </Box>
                    </Stack>
                    <Divider />
                    <Grid container spacing={1}>
                        <Grid item xs={4}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Proventos
                            </Typography>
                            <Typography fontWeight={700} color="success.main">
                                {moeda(item.proventos)}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Descontos
                            </Typography>
                            <Typography fontWeight={700} color="warning.main">
                                {moeda(item.descontos)}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Líquido
                            </Typography>
                            <Typography fontWeight={700} color="primary.main">
                                {moeda(item.liquido)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Stack>
            </CardContent>
        </Card>
    );
}

function GraficoVazio({mensagem}) {
    return (
        <Box
            sx={{
                height: 280,
                display: "grid",
                placeItems: "center",
                color: "text.secondary",
                textAlign: "center"
            }}
        >
            <Box>
                <Icon sx={{fontSize: 40, mb: 1}}>query_stats</Icon>
                <Typography variant="body2">{mensagem}</Typography>
            </Box>
        </Box>
    );
}

function DashboardEnviosPage() {
    const theme = useTheme();
    const permiteFolha = canAccessFolha();
    const permiteFinanceira = canAccessFinanceira();
    const permiteGeral = canAccessMaster();
    const modulosDisponiveis = useMemo(
        () =>
            MODULOS.filter(
                (item) =>
                    (item.valor === "FOLHA" && permiteFolha) ||
                    (item.valor === "FINANCEIRA" && permiteFinanceira) ||
                    (item.valor === "GERAL" && permiteGeral)
            ),
        [permiteFinanceira, permiteFolha, permiteGeral]
    );
    const [edicao, setEdicao] = useState(filtroPadrao);
    const [filtros, setFiltros] = useState(edicao);
    const exibirFolha =
        permiteFolha &&
        (filtros.modulos.length === 0 || filtros.modulos.includes("FOLHA"));
    const [geradoEm, setGeradoEm] = useState(() => new Date());
    const porCompetencia = edicao.tipoConsulta === "COMPETENCIA";
    const erroFiltro = porCompetencia
        ? /^[0-9]{4}-(0[1-9]|1[0-2])$/.test(edicao.competencia) &&
          !edicao.competencia.startsWith("0000")
            ? ""
            : "Informe uma competência válida (mês e ano)"
        : validarPeriodo(edicao.dataInicial, edicao.dataFinal);
    const {data, isLoading, isFetching, error} = useDashboardEnvios(
        parametrosConsulta(filtros)
    );

    const estados = data?.estados || [];
    const serieTemporal = data?.serieTemporal || [];
    const folhaPorDestino = data?.folha?.porDestino || [];
    const folhaPorCategoria = data?.folha?.porCategoria || [];
    const possuiDados = Boolean(data?.totalOperacoes);
    const modulosSelecionados = useMemo(
        () => new Set(edicao.modulos),
        [edicao.modulos]
    );

    function alterarCampo(campo, valor) {
        setEdicao((atual) => ({...atual, [campo]: valor}));
    }

    function aplicarFiltros(event) {
        event.preventDefault();
        if (!erroFiltro) {
            setFiltros({...edicao});
        }
    }

    function imprimirRelatorio() {
        setGeradoEm(new Date());
        window.setTimeout(() => window.print(), 0);
    }

    return (
        <Page maxWidth="xl">
            <GlobalStyles
                styles={{
                    "@page": {
                        size: "A4 landscape",
                        margin: "11mm"
                    },
                    "@media print": {
                        ".dashboard-actions, .dashboard-filters, .dashboard-screen-header, .MuiAppBar-root, main > .MuiToolbar-root, .ReactQueryDevtools, [aria-label='Open React Query Devtools']":
                            {
                                display: "none !important"
                            },
                        "html, body, #root": {
                            background: "#fff !important"
                        },
                        body: {
                            margin: "0 !important",
                            color: "#17211b !important",
                            WebkitPrintColorAdjust: "exact",
                            printColorAdjust: "exact"
                        },
                        main: {
                            padding: "0 !important"
                        },
                        ".MuiContainer-root": {
                            maxWidth: "none !important",
                            padding: "0 !important"
                        },
                        ".dashboard-print-header": {
                            display: "block !important"
                        },
                        ".dashboard-destination-alert": {
                            border: "1px solid #9cb8a3 !important",
                            background: "#f4f8f5 !important",
                            color: "#213a28 !important",
                            marginBottom: "10px !important"
                        },
                        ".dashboard-content": {
                            paddingBottom: "0 !important",
                            gap: "10px !important"
                        },
                        ".dashboard-kpi .MuiCardContent-root": {
                            minHeight: "66px !important",
                            padding: "8px !important"
                        },
                        ".dashboard-kpi .MuiIcon-root": {
                            fontSize: "19px !important",
                            marginBottom: "1px !important"
                        },
                        ".dashboard-kpi .MuiTypography-h4": {
                            fontSize: "1.35rem !important"
                        },
                        ".dashboard-kpi .MuiTypography-h6": {
                            fontSize: "1rem !important"
                        },
                        ".dashboard-section": {
                            breakInside: "avoid",
                            boxShadow: "none !important",
                            borderColor: "#b8c3bb !important"
                        },
                        ".dashboard-section .MuiTypography-h6": {
                            fontSize: "1rem !important"
                        },
                        ".dashboard-period-chart": {
                            height: "228px !important",
                            marginTop: "6px !important"
                        },
                        ".dashboard-financial-chart": {
                            height: "185px !important",
                            marginTop: "6px !important"
                        },
                        ".dashboard-financial-section": {
                            breakBefore: "page"
                        },
                        ".dashboard-events-section .MuiTableCell-root": {
                            padding: "4px 8px !important",
                            fontSize: "0.72rem !important"
                        },
                        ".dashboard-print-footer": {
                            display: "flex !important"
                        }
                    }
                }}
            />
            <Box className="dashboard-screen-header">
                <PageHeader
                    primary="Dashboard de envios"
                    secondary="Visão unificada das operações registradas pelo conector, com consulta por período ou competência"
                    commands={[
                        <Button
                            key="csv"
                            className="dashboard-actions"
                            variant="outlined"
                            startIcon={<Icon>download</Icon>}
                            disabled={!data}
                            onClick={() =>
                                baixarCsvDashboard(data, {
                                    incluirFolha: exibirFolha
                                })
                            }
                        >
                            Exportar CSV
                        </Button>,
                        <Button
                            key="pdf"
                            className="dashboard-actions"
                            variant="outlined"
                            startIcon={<Icon>picture_as_pdf</Icon>}
                            disabled={!data}
                            onClick={imprimirRelatorio}
                        >
                            Imprimir / salvar PDF
                        </Button>
                    ]}
                />
            </Box>

            <Box className="dashboard-print-header" sx={{display: "none"}}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{
                        borderBottom: "3px solid",
                        borderColor: "primary.main",
                        pb: 1.25
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                            component="img"
                            src={`${process.env.PUBLIC_URL}/favicon-esfinge.png`}
                            alt=""
                            sx={{width: 44, height: 44, objectFit: "contain"}}
                        />
                        <Box>
                            <Typography variant="h5" fontWeight={700}>
                                Relatório consolidado de envios
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {nomeAplicacaoNeutro(NOME_APP)} · Defensoria
                                Pública-Geral do Estado de Mato Grosso do Sul
                            </Typography>
                        </Box>
                    </Stack>
                    <Chip
                        label={obterAmbienteAplicacao(AMBIENTE).rotulo}
                        color="primary"
                        variant="outlined"
                        size="small"
                    />
                </Stack>
                <Grid container spacing={1.5} sx={{py: 1.25}}>
                    <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">
                            {filtros.tipoConsulta === "COMPETENCIA"
                                ? "Competência"
                                : "Período de registro"}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                            {rotuloConsulta(filtros)}
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">
                            Módulos
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                            {selecaoExibicao(
                                filtros.modulos,
                                modulosDisponiveis,
                                "Todos os módulos autorizados"
                            )}
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">
                            Destinos
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                            {selecaoExibicao(
                                filtros.destinos,
                                DESTINOS,
                                "Todos os destinos"
                            )}
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">
                            Emitido em
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                            {geradoEm.toLocaleString("pt-BR")}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            <Paper
                className="dashboard-filters"
                component="form"
                variant="outlined"
                onSubmit={aplicarFiltros}
                sx={{mb: 2, overflow: "hidden"}}
            >
                <Stack
                    direction={{xs: "column", sm: "row"}}
                    justifyContent="space-between"
                    alignItems={{xs: "flex-start", sm: "center"}}
                    spacing={1}
                    sx={{px: 2.5, py: 1.75, bgcolor: "grey.50"}}
                >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                            sx={{
                                width: 38,
                                height: 38,
                                display: "grid",
                                placeItems: "center",
                                borderRadius: 1.5,
                                bgcolor: "primary.main",
                                color: "primary.contrastText"
                            }}
                        >
                            <Icon>filter_alt</Icon>
                        </Box>
                        <Box>
                            <Typography fontWeight={700}>
                                Filtros do relatório
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Escolha o período de registro ou a competência e
                                refine por módulo ou destino.
                            </Typography>
                        </Box>
                    </Stack>
                    <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        icon={<Icon>date_range</Icon>}
                        label={`${
                            filtros.tipoConsulta === "COMPETENCIA"
                                ? "Competência"
                                : "Período"
                        }: ${rotuloConsulta(filtros)}`}
                    />
                </Stack>
                <Divider />
                <Grid
                    container
                    spacing={2}
                    alignItems="flex-start"
                    sx={{px: 2.5, pt: 2.5, pb: 1.5}}
                >
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField
                            fullWidth
                            select
                            label="Buscar por"
                            value={edicao.tipoConsulta}
                            onChange={(event) =>
                                alterarCampo("tipoConsulta", event.target.value)
                            }
                        >
                            <MenuItem value="PERIODO">Período</MenuItem>
                            <MenuItem value="COMPETENCIA">Competência</MenuItem>
                        </TextField>
                    </Grid>
                    {porCompetencia ? (
                        <Grid item xs={12} sm={6} lg={4}>
                            <TextField
                                fullWidth
                                required
                                type="month"
                                label="Competência"
                                InputLabelProps={{shrink: true}}
                                inputProps={{min: "0001-01", max: "9999-12"}}
                                value={edicao.competencia}
                                onChange={(event) =>
                                    alterarCampo(
                                        "competencia",
                                        event.target.value
                                    )
                                }
                                error={Boolean(erroFiltro)}
                                helperText="Apenas ocorrências com competência informada, independentemente da data de registro."
                            />
                        </Grid>
                    ) : (
                        <>
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    required
                                    type="date"
                                    label="Data inicial"
                                    InputLabelProps={{shrink: true}}
                                    value={edicao.dataInicial}
                                    onChange={(event) =>
                                        alterarCampo(
                                            "dataInicial",
                                            event.target.value
                                        )
                                    }
                                    error={Boolean(erroFiltro)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    required
                                    type="date"
                                    label="Data final"
                                    InputLabelProps={{shrink: true}}
                                    value={edicao.dataFinal}
                                    onChange={(event) =>
                                        alterarCampo(
                                            "dataFinal",
                                            event.target.value
                                        )
                                    }
                                    error={Boolean(erroFiltro)}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12} sm={6} lg={3}>
                        <TextField
                            fullWidth
                            select
                            label="Módulos"
                            InputLabelProps={{shrink: true}}
                            value={edicao.modulos}
                            SelectProps={{
                                multiple: true,
                                displayEmpty: true,
                                renderValue: (valores) =>
                                    valores.length > 1
                                        ? `${valores.length} módulos selecionados`
                                        : valores.length === 0 &&
                                          modulosDisponiveis.length === 1
                                        ? modulosDisponiveis[0].rotulo
                                        : selecaoExibicao(
                                              valores,
                                              modulosDisponiveis,
                                              "Todos os módulos autorizados"
                                          )
                            }}
                            disabled={modulosDisponiveis.length <= 1}
                            onChange={(event) =>
                                alterarCampo("modulos", event.target.value)
                            }
                        >
                            {modulosDisponiveis.map((item) => (
                                <MenuItem key={item.valor} value={item.valor}>
                                    <Checkbox
                                        checked={modulosSelecionados.has(
                                            item.valor
                                        )}
                                    />
                                    {item.rotulo}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <TextField
                            fullWidth
                            select
                            label="Destinos"
                            InputLabelProps={{shrink: true}}
                            value={edicao.destinos}
                            SelectProps={{
                                multiple: true,
                                displayEmpty: true,
                                renderValue: (valores) =>
                                    valores.length > 1
                                        ? `${valores.length} destinos selecionados`
                                        : selecaoExibicao(
                                              valores,
                                              DESTINOS,
                                              "Todos os destinos"
                                          )
                            }}
                            onChange={(event) =>
                                alterarCampo("destinos", event.target.value)
                            }
                        >
                            {DESTINOS.map((item) => (
                                <MenuItem key={item.valor} value={item.valor}>
                                    <Checkbox
                                        checked={edicao.destinos.includes(
                                            item.valor
                                        )}
                                    />
                                    {item.rotulo}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {erroFiltro && (
                        <Grid item xs={12} sx={{pt: "8px !important"}}>
                            <Typography
                                variant="caption"
                                color="error.main"
                                role="alert"
                            >
                                {erroFiltro}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
                <Divider />
                <Stack
                    direction={{xs: "column", sm: "row"}}
                    justifyContent="space-between"
                    alignItems={{xs: "stretch", sm: "center"}}
                    spacing={1.5}
                    sx={{px: 2.5, py: 1.5}}
                >
                    <FormControlLabel
                        sx={{m: 0}}
                        control={
                            <Checkbox
                                checked={edicao.incluirArquivados}
                                onChange={(event) =>
                                    alterarCampo(
                                        "incluirArquivados",
                                        event.target.checked
                                    )
                                }
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2" fontWeight={600}>
                                    Incluir ocorrências arquivadas
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Inclui o histórico arquivado; canceladas
                                    permanecem fora dos totais vigentes.
                                </Typography>
                            </Box>
                        }
                    />
                    <Box sx={{minWidth: {sm: 210}}}>
                        <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            startIcon={<Icon>filter_alt</Icon>}
                            disabled={Boolean(erroFiltro) || isFetching}
                            sx={{minHeight: 44}}
                        >
                            Aplicar filtros
                        </Button>
                    </Box>
                </Stack>
            </Paper>

            <Alert
                className="dashboard-destination-alert"
                severity="info"
                sx={{mb: 2}}
            >
                O recebimento pelo <strong>Fornecedor</strong> e o aceite
                oficial do TCE-MS são etapas distintas. Confira o protocolo de
                entrega e a evidência do aceite oficial no detalhe da
                ocorrência.
            </Alert>

            {Number(data?.aguardandoRevisao || 0) > 0 && (
                <Alert
                    className="dashboard-review-alert"
                    severity="warning"
                    sx={{mb: 2}}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            href="/?estados=AGUARDANDO_APROVACAO"
                        >
                            Revisar ocorrências
                        </Button>
                    }
                >
                    <strong>
                        {numero(data.aguardandoRevisao)}{" "}
                        {Number(data.aguardandoRevisao) === 1
                            ? "preparação"
                            : "preparações"}
                    </strong>{" "}
                    {Number(data.aguardandoRevisao) === 1
                        ? "aguarda"
                        : "aguardam"}{" "}
                    conferência e autorização. A preparação não representa
                    transmissão ao e-Sfinge ou ao fornecedor.
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{mb: 2}}>
                    Não foi possível carregar o dashboard. Revise os filtros e
                    tente novamente.
                </Alert>
            )}

            {isLoading ? (
                <Box
                    sx={{minHeight: 360, display: "grid", placeItems: "center"}}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Stack className="dashboard-content" spacing={2} sx={{pb: 6}}>
                    <Alert severity="info">
                        Operações canceladas permanecem no histórico, mas não
                        compõem as conclusões vigentes, os registros processados
                        nem os valores da folha. Solicitações de cancelamento
                        são acompanhadas na lista de ocorrências.
                    </Alert>
                    <Grid container spacing={2} columns={{xs: 12, md: 14}}>
                        <Grid item xs={6} md={2}>
                            <Kpi
                                icone="send"
                                rotulo="Operações"
                                valor={data?.totalOperacoes}
                            />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Kpi
                                icone="fact_check"
                                rotulo="Aguardando revisão"
                                valor={data?.aguardandoRevisao}
                                cor="warning.main"
                            />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Kpi
                                icone="task_alt"
                                rotulo="Concluídas vigentes"
                                valor={data?.concluidas}
                                cor="success.main"
                            />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Kpi
                                icone="cancel"
                                rotulo="Canceladas"
                                valor={data?.canceladas}
                                cor="text.secondary"
                            />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Kpi
                                icone="sync"
                                rotulo="Em processamento"
                                valor={data?.emAndamento}
                                cor="info.main"
                            />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Kpi
                                icone="error_outline"
                                rotulo="Com atenção"
                                valor={data?.comAtencao}
                                cor="error.main"
                            />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Kpi
                                icone="format_list_numbered"
                                rotulo="Registros processados"
                                valor={data?.registrosProcessados}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={8}>
                            <Paper
                                className="dashboard-section"
                                variant="outlined"
                                sx={{p: 2, height: "100%"}}
                            >
                                <Typography variant="h6">
                                    Operações por data de registro e destino
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Cada ocorrência-raiz é contabilizada uma
                                    única vez, na data em que foi registrada.
                                </Typography>
                                {possuiDados ? (
                                    <Box
                                        className="dashboard-period-chart"
                                        sx={{height: 300, mt: 2}}
                                    >
                                        <ResponsiveContainer
                                            width="100%"
                                            height="100%"
                                        >
                                            <BarChart data={serieTemporal}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="rotulo" />
                                                <YAxis allowDecimals={false} />
                                                <Tooltip />
                                                <Legend />
                                                <Bar
                                                    dataKey="esfingeDireto"
                                                    name="e-Sfinge direto"
                                                    stackId="destino"
                                                    fill={
                                                        theme.palette.primary
                                                            .main
                                                    }
                                                />
                                                <Bar
                                                    dataKey="fornecedor"
                                                    name="Fornecedor"
                                                    stackId="destino"
                                                    fill="#d18b1f"
                                                />
                                                <Bar
                                                    dataKey="outros"
                                                    name="Outros"
                                                    stackId="destino"
                                                    fill="#9e9e9e"
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                ) : (
                                    <GraficoVazio mensagem="Nenhuma operação para os filtros selecionados." />
                                )}
                            </Paper>
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <Paper
                                className="dashboard-section"
                                variant="outlined"
                                sx={{p: 2, height: "100%"}}
                            >
                                <Typography variant="h6">
                                    Situação das operações
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Situação atual, considerando cancelamentos
                                    concluídos.
                                </Typography>
                                {estados.length ? (
                                    <Box
                                        className="dashboard-period-chart"
                                        sx={{height: 300, mt: 2}}
                                    >
                                        <ResponsiveContainer
                                            width="100%"
                                            height="100%"
                                        >
                                            <PieChart>
                                                <Pie
                                                    data={estados}
                                                    dataKey="quantidade"
                                                    nameKey="rotulo"
                                                    innerRadius={58}
                                                    outerRadius={95}
                                                    paddingAngle={2}
                                                >
                                                    {estados.map((item) => (
                                                        <Cell
                                                            key={item.chave}
                                                            fill={
                                                                CORES_ESTADO[
                                                                    item.chave
                                                                ] || "#78909c"
                                                            }
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    formatter={(valor) =>
                                                        numero(valor)
                                                    }
                                                />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                ) : (
                                    <GraficoVazio mensagem="Sem estados para consolidar." />
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {exibirFolha && (
                        <ResumoFolhaCompetencia
                            competencia={
                                filtros.tipoConsulta === "COMPETENCIA"
                                    ? filtros.competencia.replace("-", "")
                                    : undefined
                            }
                        />
                    )}

                    {exibirFolha && (
                        <Paper
                            className="dashboard-section dashboard-financial-section"
                            variant="outlined"
                            sx={{p: 2}}
                        >
                            <Stack
                                direction={{xs: "column", md: "row"}}
                                justifyContent="space-between"
                                spacing={1}
                            >
                                <Box>
                                    <Typography variant="h6">
                                        Consolidação financeira das folhas
                                        concluídas
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Valores extraídos apenas de fluxos de
                                        folha concluídos e não cancelados, sem
                                        duplicar os lotes parciais.
                                    </Typography>
                                </Box>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap="wrap"
                                >
                                    {(data?.modulos || []).map((item) => (
                                        <Chip
                                            key={item.chave}
                                            label={`${item.rotulo}: ${numero(
                                                item.quantidade
                                            )}`}
                                            variant="outlined"
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                            <Grid container spacing={2} sx={{mt: 0.5}}>
                                <Grid item xs={6} md={2}>
                                    <Kpi
                                        icone="receipt_long"
                                        rotulo="Folhas concluídas"
                                        valor={data?.folha?.folhasConcluidas}
                                    />
                                </Grid>
                                <Grid item xs={6} md={2}>
                                    <Kpi
                                        icone="groups"
                                        rotulo="Servidores"
                                        valor={data?.folha?.servidores}
                                    />
                                </Grid>
                                <Grid item xs={6} md={2}>
                                    <Kpi
                                        icone="format_list_numbered"
                                        rotulo="Lançamentos"
                                        valor={data?.folha?.lancamentos}
                                    />
                                </Grid>
                                <Grid item xs={6} md={2}>
                                    <KpiMoeda
                                        rotulo="Proventos"
                                        valor={data?.folha?.proventos}
                                        cor="success.main"
                                    />
                                </Grid>
                                <Grid item xs={6} md={2}>
                                    <KpiMoeda
                                        rotulo="Descontos"
                                        valor={data?.folha?.descontos}
                                        cor="warning.main"
                                    />
                                </Grid>
                                <Grid item xs={6} md={2}>
                                    <KpiMoeda
                                        rotulo="Resultado líquido"
                                        valor={data?.folha?.liquido}
                                    />
                                </Grid>
                            </Grid>
                            {folhaPorCategoria.length > 0 && (
                                <Box sx={{mt: 2}}>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                        sx={{mb: 1}}
                                    >
                                        Valores por grupo de beneficiários
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {folhaPorCategoria.map((item) => (
                                            <Grid
                                                item
                                                xs={12}
                                                md={4}
                                                key={item.categoria}
                                            >
                                                <ResumoCategoriaFolha
                                                    item={item}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}
                            {folhaPorDestino.length > 0 && (
                                <Box
                                    className="dashboard-financial-chart"
                                    sx={{height: 280, mt: 2}}
                                >
                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                    >
                                        <BarChart data={folhaPorDestino}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="rotulo" />
                                            <YAxis
                                                tickFormatter={(valor) =>
                                                    numero(valor)
                                                }
                                            />
                                            <Tooltip
                                                formatter={(valor) =>
                                                    moeda(valor)
                                                }
                                            />
                                            <Legend />
                                            <Bar
                                                dataKey="proventos"
                                                name="Proventos"
                                                fill="#2e7d32"
                                            />
                                            <Bar
                                                dataKey="descontos"
                                                name="Descontos"
                                                fill="#ed6c02"
                                            />
                                            <Bar
                                                dataKey="liquido"
                                                name="Líquido"
                                                fill={
                                                    theme.palette.primary.main
                                                }
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            )}
                        </Paper>
                    )}

                    <Paper
                        className="dashboard-section dashboard-events-section"
                        variant="outlined"
                        sx={{overflow: "hidden"}}
                    >
                        <Box sx={{p: 2}}>
                            <Typography variant="h6">
                                Eventos mais frequentes
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Até dez tipos de operação com maior volume na
                                consulta.
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Evento</TableCell>
                                        <TableCell>Módulo</TableCell>
                                        <TableCell align="right">
                                            Operações
                                        </TableCell>
                                        <TableCell align="right">
                                            Concluídas vigentes
                                        </TableCell>
                                        <TableCell align="right">
                                            Com atenção
                                        </TableCell>
                                        <TableCell align="right">
                                            Canceladas
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(data?.eventos || []).map((item) => (
                                        <TableRow key={item.evento}>
                                            <TableCell sx={{fontWeight: 600}}>
                                                {item.evento}
                                            </TableCell>
                                            <TableCell>{item.modulo}</TableCell>
                                            <TableCell align="right">
                                                {numero(item.quantidade)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {numero(item.concluidas)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {numero(item.comAtencao)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {numero(item.canceladas)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {!data?.eventos?.length && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                align="center"
                                            >
                                                Nenhum evento para os filtros
                                                selecionados.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    <Box
                        className="dashboard-print-footer"
                        sx={{
                            display: "none",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderTop: "1px solid",
                            borderColor: "divider",
                            pt: 1,
                            color: "text.secondary"
                        }}
                    >
                        <Typography variant="caption">
                            Relatório consultivo gerado a partir das ocorrências
                            registradas pelo conector.
                        </Typography>
                        <Typography variant="caption">
                            O aceite do fornecedor não substitui o aceite final
                            do e-Sfinge/TCE-MS.
                        </Typography>
                    </Box>
                </Stack>
            )}
        </Page>
    );
}

function KpiMoeda({rotulo, valor, cor = "primary.main"}) {
    return (
        <Card
            className="dashboard-kpi"
            variant="outlined"
            sx={{height: "100%"}}
        >
            <CardContent
                sx={{
                    minHeight: 118,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    "&:last-child": {pb: 2}
                }}
            >
                <Typography
                    variant="h6"
                    component="p"
                    fontWeight={700}
                    color={cor}
                >
                    {moeda(valor)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {rotulo}
                </Typography>
            </CardContent>
        </Card>
    );
}

export default DashboardEnviosPage;
